import React from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import {
    Button,
    Alert,
    DatePicker,
    Select,
    FormCheckbox,
} from "@jhool-io/fe-components";
import { useFetchUsersList } from "../../../../hooks/queries/user-management";
import { formatZonedTimeToUtc } from "../../../../utils/helpers";
import { IUpdateTask, TaskStatus } from "../../../../utils/types/tasks";
import { useUpdateTask } from "../../../../hooks/mutations/tasks";
import useToast from "../../../../hooks/useToast";
import { AddTextEditor } from "../../../../components/TextEditor/AddTextEditor/AddTextEditor";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import { ITask } from "../../types/tasks.types";

interface ReassignTaskProps {
    // Function to call when form submit button is clicked
    onFormSubmit(): void;
    /**
     * Current action
     */
    action?: "reassign" | "forward";
    task: ITask;
}

const schema = yup.object({
    due_date: yup.date(),
    description: yup.string(),
    assignee_user_ids: yup.array().required("Field is required"),
    viewer_user_ids: yup.array(),
});

export default function ReassignTask({
    onFormSubmit,
    action = "reassign",
    task,
}: ReassignTaskProps) {
    const loggedInUser = useFetchUserDetails();

    const [internalUserSearchString, setInternalUserSearchString] =
        React.useState("");
    const [showAssigneeOptions, setShowAssigneeOptions] = React.useState(false);
    const [viewerUserSearchString, setViewerUserSearchString] =
        React.useState("");
    const [showViewerOptions, setShowViewerOptions] = React.useState(false);
    const [shoudlAllowLessThanTwoHours, setShouldAllowLessThanTwoHours] =
        React.useState<null | "asked" | "allow">(null);
    const [taskDescription, setTaskDescription] = React.useState("");
    const [assignToMe, setAssignToMe] = React.useState(() =>
        Boolean(
            task?.assignees?.find(
                (assignee) => assignee.user_id === loggedInUser?.data?.user_id
            )
        )
    );
    const [addMeAsViewer, setAddMeAsViewer] = React.useState(() =>
        Boolean(
            task?.viewers?.find(
                (viewer) => viewer.user_id === loggedInUser?.data?.user_id
            )
        )
    );

    // React hook form values
    const {
        handleSubmit,
        control,
        formState: { errors },
        watch,
    } = useForm<IUpdateTask>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: task?.assignees
            ? {
                  ...task,
                  assignee_user_ids:
                      action === "reassign"
                          ? task.assignees?.map((user) => user.user_id)
                          : undefined,
              }
            : undefined,
    });

    // Local component states
    const [localDueDate, setLocalDueDate] = React.useState<Date | null>(
        task?.due_date ? parseISO(task.due_date) : null
    );

    const debouncedUserSearchString = useDebounce(
        internalUserSearchString,
        500
    );

    const debouncedViewerSearchString = useDebounce(
        viewerUserSearchString,
        500
    );

    // Fetch providers
    const internalUsersList = useFetchUsersList(
        {
            search_string: debouncedUserSearchString.toLowerCase(),
        },
        showAssigneeOptions && internalUserSearchString.length > 0
    );

    const viewerUsersList = useFetchUsersList(
        {
            search_string: debouncedViewerSearchString.toLowerCase(),
        },
        showViewerOptions && viewerUserSearchString.length > 0
    );

    const InternalUsersForSelect = internalUsersList.data?.data.map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.user_id,
    }));

    // Get filtered options for users list
    const filteredUsers = InternalUsersForSelect?.filter((option) =>
        option.label
            .toLowerCase()
            .includes(internalUserSearchString.toLowerCase())
    );

    const ViewerUsersForSelect = viewerUsersList.data?.data.map((user) => ({
        label: `${user.first_name} ${user.last_name}`,
        value: user.user_id,
    }));

    const filteredViewers = ViewerUsersForSelect?.filter((option) =>
        option.label
            .toLowerCase()
            .includes(viewerUserSearchString.toLowerCase())
    );

    const assigneeIds = watch("assignee_user_ids");
    const viewerIds = watch("viewer_user_ids");

    const queryClient = useQueryClient();

    const { toast } = useToast();

    const update = useUpdateTask();

    const onSubmit = (values: IUpdateTask) => {
        const payload = {
            todos_to_update: [
                {
                    ...values,
                    todo_id: task.todo_id as string,
                    status: task.status as TaskStatus,
                    title: task.title as string,
                    description: taskDescription || undefined,
                    due_date: values.due_date
                        ? formatZonedTimeToUtc(values.due_date)
                        : undefined,
                },
            ],
        };

        if (assignToMe) {
            payload.todos_to_update[0].assignee_user_ids =
                payload.todos_to_update[0].assignee_user_ids &&
                payload.todos_to_update[0].assignee_user_ids.length > 0
                    ? Array.from(
                          new Set([
                              ...payload.todos_to_update[0].assignee_user_ids,
                              loggedInUser?.data?.user_id as string,
                          ])
                      )
                    : [loggedInUser?.data?.user_id as string];
        } else {
            payload.todos_to_update[0].assignee_user_ids = Array.from(
                (payload.todos_to_update[0].assignee_user_ids || []).filter(
                    (id) => id !== loggedInUser?.data?.user_id
                )
            );
        }

        if (addMeAsViewer && !assignToMe) {
            payload.todos_to_update[0].viewer_user_ids =
                payload.todos_to_update[0].viewer_user_ids &&
                payload.todos_to_update[0].viewer_user_ids.length > 0
                    ? Array.from(
                          new Set([
                              ...payload.todos_to_update[0].viewer_user_ids,
                              loggedInUser?.data?.user_id as string,
                          ])
                      )
                    : [loggedInUser?.data?.user_id as string];
        } else {
            payload.todos_to_update[0].viewer_user_ids = Array.from(
                new Set(
                    (payload.todos_to_update[0].viewer_user_ids || []).filter(
                        (id) => id !== loggedInUser?.data?.user_id
                    )
                )
            );
        }

        update.mutate(
            {
                ...payload,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: ["todos"],
                    });

                    toast({
                        mode: "success",
                        message: "Task updated successfully!",
                    });
                    onFormSubmit();
                },

                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            "Could not update the task",
                    });
                },
            }
        );
    };

    const popoverLabel = action === "forward" ? "Forward to" : "Re-assign to";

    const popoverPlaceholder =
        action === "forward"
            ? "Forward to (Type multiple)"
            : "Re-assign to (Type multiple)";

    return (
        <form id="reassign-task" onSubmit={handleSubmit(onSubmit)}>
            <p className="text-base font-medium mb-3">
                {action === "reassign" &&
                    "Select one or more users to reassign this task to"}
                {action === "forward" &&
                    "Select one or more users to forward this task to"}
            </p>

            <div className="fg">
                <Controller
                    name="assignee_user_ids"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label={popoverLabel}
                            placeholder={popoverPlaceholder}
                            isMulti
                            isSearchable
                            filterOption={() => true}
                            onChange={(val) => {
                                field.onChange(
                                    (
                                        val as {
                                            label: string;
                                            value: string;
                                        }[]
                                    ).map((code) => code.value)
                                );
                            }}
                            onInputChange={(value) => {
                                setInternalUserSearchString(value);
                                setShowAssigneeOptions(true);
                            }}
                            onBlur={() => {
                                setShowAssigneeOptions(false);
                            }}
                            hasError={!!errors.assignee_user_ids}
                            errorText={errors.assignee_user_ids?.message}
                            options={filteredUsers}
                            noOptionsMessage={() =>
                                internalUsersList.isLoading
                                    ? "Loading..."
                                    : "No options"
                            }
                            multiHasValues={
                                assigneeIds && assigneeIds.length > 0
                            }
                            isLongListInDialog
                            defaultValue={
                                action === "reassign"
                                    ? InternalUsersForSelect?.filter((user) =>
                                          field.value?.includes(user.value)
                                      ) || null
                                    : null
                            }
                            menuIsOpen={internalUserSearchString.length > 0}
                            openMenuOnClick={false}
                        />
                    )}
                />
                {!addMeAsViewer ? (
                    <div className="mt-2">
                        <FormCheckbox
                            label={
                                action === "reassign"
                                    ? "Re-assign to me"
                                    : "Forward to me"
                            }
                            labelClass="!text-textSec"
                            checked={assignToMe}
                            onChange={(e) => setAssignToMe(e.target.checked)}
                        />
                    </div>
                ) : null}
            </div>

            <div className="fg">
                <Controller
                    name="viewer_user_ids"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Viewers"
                            placeholder="Viewers (Type multiple)"
                            isMulti
                            isSearchable
                            filterOption={() => true}
                            onChange={(val) => {
                                field.onChange(
                                    (
                                        val as {
                                            label: string;
                                            value: string;
                                        }[]
                                    ).map((code) => code.value)
                                );
                            }}
                            onInputChange={(value) => {
                                setViewerUserSearchString(value);
                                setShowViewerOptions(true);
                            }}
                            onBlur={() => {
                                setShowViewerOptions(false);
                            }}
                            hasError={!!errors.viewer_user_ids}
                            errorText={errors.viewer_user_ids?.message}
                            options={filteredViewers}
                            noOptionsMessage={() =>
                                viewerUsersList.isLoading
                                    ? "Loading..."
                                    : "No options"
                            }
                            multiHasValues={viewerIds && viewerIds.length > 0}
                            isLongListInDialog
                            menuIsOpen={viewerUserSearchString.length > 0}
                            openMenuOnClick={false}
                        />
                    )}
                />
                {!assignToMe ? (
                    <div className="mt-2">
                        <FormCheckbox
                            label="Add self to viewers"
                            labelClass="!text-textSec"
                            checked={addMeAsViewer}
                            onChange={(e) => setAddMeAsViewer(e.target.checked)}
                        />
                    </div>
                ) : null}
            </div>

            {action === "forward" ? (
                <>
                    <div className="fg">
                        <Controller
                            name="due_date"
                            control={control}
                            render={({ field }) => (
                                <div className="fg">
                                    <DatePicker
                                        label="Due date"
                                        selected={localDueDate}
                                        minDate={new Date(Date.now())}
                                        hasError={!!errors?.due_date}
                                        onChange={(date) => {
                                            field.onChange(date);
                                            setLocalDueDate(date);
                                        }}
                                        errorText={
                                            errors?.due_date?.type ===
                                            "typeError"
                                                ? "Invalid date value"
                                                : errors?.due_date?.message
                                        }
                                        popperPlacement="auto"
                                    />
                                </div>
                            )}
                        />
                    </div>

                    <AddTextEditor
                        title="Add description"
                        onEditorTextChange={setTaskDescription}
                        isRequiredFieldProvided
                        defaultValue={task?.description || ""}
                    />
                </>
            ) : null}

            {shoudlAllowLessThanTwoHours === "asked" ? (
                <div className="flex flex-col mt-[--spacing-form-items] gap-[--spacing-items] md:flex-row items-center">
                    <Alert
                        type="warning"
                        description="This task is due in less than two hours, and you won't get a reminder. Are you sure you want to continue?"
                    />

                    <Button
                        variant="primary"
                        size="small"
                        onClick={() => setShouldAllowLessThanTwoHours("allow")}
                        type="button"
                    >
                        Continue
                    </Button>
                </div>
            ) : null}
        </form>
    );
}
